import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import { Section, Field } from '../components/Section';
import { WorkHoursEditor } from '../components/WorkHoursEditor';
import { DomainsEditor } from '../components/DomainsEditor';
import { toast } from '../components/Toast';
import { LivePulse } from '../ui/LivePulse';
import { Pill } from '../ui/Pill';
import { Icon } from '../ui/Icons';
import type { Agent, Tenant, WorkHours, WidgetSchedule, DayKey } from '../lib/types';

// MVP scope (per CLAUDE.md): one agent per tenant. When v1 introduces multi-agent
// UI, this page becomes the per-agent editor with an agent selector at the top.

export const AgentSettings = () => {
  const qc = useQueryClient();

  const agentsQ = useQuery<{ agents: Agent[] }>({
    queryKey: ['agents'],
    queryFn: async () => (await api.get('/api/tenant/agents')).data,
  });
  const tenantQ = useQuery<{ tenant: Tenant }>({
    queryKey: ['tenant'],
    queryFn: async () => (await api.get('/api/tenant/me')).data,
  });

  const agent = agentsQ.data?.agents[0];
  const tenant = tenantQ.data?.tenant;

  const [form, setForm] = useState<FormState | null>(null);
  useEffect(() => {
    if (agent && tenant && !form) setForm(toFormState(agent, tenant));
  }, [agent, tenant, form]);

  const agentMut = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      if (!agent) throw new Error('no agent');
      return (await api.patch(`/api/tenant/agents/${agent.id}`, patch)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agents'] });
      toast.success('Agent updated');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const tenantMut = useMutation({
    mutationFn: async (patch: Record<string, unknown>) =>
      (await api.patch('/api/tenant/me', patch)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant'] });
      toast.success('Tenant updated');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (!agent || !tenant || !form) {
    return (
      <>
        <PageHeader title="Agent" subtitle="Configure how your chatbot behaves." />
        <div className="px-10 text-[12.5px] text-ink-subtle">Loading…</div>
      </>
    );
  }

  const saveBasics = () =>
    agentMut.mutate({
      name: form.name,
      persona: form.persona || null,
      widgetWelcomeMsg: form.welcome || null,
      widgetPrimaryColor: form.primary,
      widgetAvatarUrl: form.avatarUrl || null,
      widgetLauncherStyle: form.launcherStyle,
      widgetLauncherLabel: form.launcherLabel.trim() || null,
      widgetOffsetBottom: form.offsetBottom,
      widgetOffsetBottomBelow: form.offsetBottomBelow.trim()
        ? Number(form.offsetBottomBelow)
        : null,
      status: form.status,
    });
  const saveDomains = () =>
    agentMut.mutate({
      allowedDomains: form.allowedDomains,
      notificationEmail: form.notificationEmail || null,
    });
  const saveIntegrations = () =>
    agentMut.mutate({
      notificationWebhook: form.notificationWebhook.trim() || null,
      // Leave the stored secret untouched unless the field was actually edited.
      ...(form.webhookSecret === SECRET_UNCHANGED
        ? {}
        : { notificationWebhookSecret: form.webhookSecret.trim() || null }),
      webhookFormName: form.webhookFormName.trim() || null,
      attributionCookies: parseCookieLines(form.attributionCookies),
      conversionEventName: form.conversionEventName.trim() || null,
      conversionRedirectUrl: form.conversionRedirectUrl.trim() || null,
      emailsHandledExternally: form.emailsHandledExternally,
    });
  const saveSchedule = () =>
    agentMut.mutate({
      widgetSchedule: form.widgetSchedule ?? null,
      widgetOfflineMessage: form.offlineMessage || null,
    });
  const saveAvailability = () =>
    tenantMut.mutate({
      workHours: form.workHours,
      responseTimeMinutes: form.responseTimeMinutes,
      inHoursReplyMessage: form.inHoursReplyMessage.trim() || null,
      outOfHoursReplyMessage: form.outOfHoursReplyMessage.trim() || null,
    });
  const saveEmailSender = () =>
    tenantMut.mutate({
      ...(form.resendApiKey === RESEND_UNCHANGED
        ? {}
        : { resendApiKey: form.resendApiKey || null }),
      resendFrom: form.resendFrom || null,
    });

  const togglePause = () =>
    setForm({ ...form, status: form.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE' });

  const tone =
    agent.status === 'ACTIVE'
      ? 'won'
      : agent.status === 'PAUSED'
        ? 'new'
        : 'muted';

  return (
    <div>
      <PageHeader
        title="Agent settings"
        subtitle={`Configure how ${agent.name} talks to visitors on your site.`}
        actions={
          <>
            {agent.status === 'ACTIVE' && <LivePulse label="Active" />}
            <Pill tone={tone} dot>{agent.status.toLowerCase()}</Pill>
            <button type="button" className="btn-ghost" onClick={togglePause}>
              {form.status === 'ACTIVE' ? 'Pause agent' : 'Activate agent'}
            </button>
          </>
        }
      />

      <div className="px-10 pb-10 grid gap-[22px] grid-cols-1 xl:grid-cols-[1fr_360px] items-start">
        {/* Left: settings */}
        <div className="flex flex-col gap-4">
          <SectionTabs sections={SECTIONS} />

          <div id="basics">
            <Section
              title="Basics"
              description="Display name, greeting, and persona."
              actions={
                <button
                  type="button"
                  className="btn-primary"
                  onClick={saveBasics}
                  disabled={agentMut.isPending}
                >
                  {agentMut.isPending ? 'Saving…' : 'Save basics'}
                </button>
              }
            >
              <Field label="Agent name">
                <input
                  className="input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </Field>
              <Field
                label="Welcome message"
                hint={`${form.welcome.length} / 200`}
              >
                <textarea
                  className="input min-h-[64px] leading-relaxed"
                  maxLength={200}
                  value={form.welcome}
                  onChange={(e) => setForm({ ...form, welcome: e.target.value })}
                />
              </Field>
              <Field label="Persona / tone" hint="Optional notes about voice, style, attitude.">
                <textarea
                  className="input min-h-[80px] leading-relaxed"
                  value={form.persona}
                  onChange={(e) => setForm({ ...form, persona: e.target.value })}
                />
                <div className="mt-2 flex gap-1.5 flex-wrap">
                  {PERSONA_SUGGESTIONS.map((s) => (
                    <button
                      key={s.text}
                      type="button"
                      onClick={() =>
                        setForm({
                          ...form,
                          persona: form.persona ? `${form.persona}\n${s.append}` : s.append,
                        })
                      }
                      className="text-[11px] py-1 px-2.5 rounded-full bg-accent-soft text-accent-ink hover:bg-accent/15 transition-colors"
                    >
                      {s.text}
                    </button>
                  ))}
                </div>
              </Field>

              <Field
                label="Closed widget"
                hint="How it looks before anyone opens it. A bare icon is tidy; a label tells a first-time visitor it is a chat."
              >
                <div className="flex flex-wrap items-center gap-2">
                  {(
                    [
                      ['bubble', 'Icon only'],
                      ['label', 'Icon with label'],
                    ] as const
                  ).map(([value, text]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setForm({ ...form, launcherStyle: value })}
                      className={
                        'text-[12px] py-1.5 px-3 rounded-full border transition-colors ' +
                        (form.launcherStyle === value
                          ? 'bg-accent-soft text-accent-ink border-accent/30'
                          : 'bg-white text-ink-subtle border-line-strong hover:bg-surface-2')
                      }
                    >
                      {text}
                    </button>
                  ))}
                </div>
                {form.launcherStyle === 'label' && (
                  <input
                    className="input mt-2"
                    maxLength={40}
                    placeholder="Talk to us"
                    value={form.launcherLabel}
                    onChange={(e) => setForm({ ...form, launcherLabel: e.target.value })}
                  />
                )}
              </Field>

              <Field
                label="Lift above the bottom edge"
                hint="Pixels to lift the widget when the page fixes something to the bottom — a sticky call bar, a cookie notice. Set the width below to match where that thing appears, or the widget floats above nothing on wider screens."
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="number"
                    min={0}
                    max={300}
                    className="input w-24"
                    value={form.offsetBottom}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        offsetBottom: Math.max(0, Math.min(300, Number(e.target.value) || 0)),
                      })
                    }
                  />
                  <span className="text-[12px] text-ink-subtle">px, only below</span>
                  <input
                    type="number"
                    min={320}
                    max={2400}
                    placeholder="all widths"
                    className="input w-28"
                    value={form.offsetBottomBelow}
                    onChange={(e) => setForm({ ...form, offsetBottomBelow: e.target.value })}
                  />
                  <span className="text-[12px] text-ink-subtle">px wide</span>
                </div>
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="Primary color"
                  hint="Match the client's site — the widget should read as their front desk, not a tool bolted onto it."
                >
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    {(
                      [
                        ['#1F2329', 'Neutral'],
                        ['#007BE2', 'Axccelerate'],
                      ] as const
                    ).map(([hex, name]) => (
                      <button
                        key={hex}
                        type="button"
                        onClick={() => setForm({ ...form, primary: hex })}
                        className={
                          'flex items-center gap-1.5 text-[11.5px] py-1 px-2 rounded-full border transition-colors ' +
                          (form.primary.toLowerCase() === hex.toLowerCase()
                            ? 'border-accent/40 bg-accent-soft text-accent-ink'
                            : 'border-line-strong bg-white text-ink-subtle hover:bg-surface-2')
                        }
                      >
                        <span
                          className="w-3 h-3 rounded-full border border-black/10"
                          style={{ background: hex }}
                        />
                        {name}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form.primary}
                      onChange={(e) => setForm({ ...form, primary: e.target.value })}
                      className="w-9 h-9 rounded-lg border border-line-strong bg-white p-0.5 cursor-pointer"
                    />
                    <input
                      className="input font-mono text-[12.5px]"
                      value={form.primary}
                      onChange={(e) => setForm({ ...form, primary: e.target.value })}
                    />
                  </div>
                </Field>
                <Field label="Avatar">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-white font-serif text-base shrink-0"
                      style={{ background: form.primary }}
                    >
                      {form.avatarUrl ? (
                        <img
                          src={form.avatarUrl}
                          alt=""
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        form.name.trim().charAt(0).toUpperCase() || '·'
                      )}
                    </div>
                    <input
                      type="url"
                      className="input"
                      placeholder="https://example.com/avatar.png"
                      value={form.avatarUrl}
                      onChange={(e) => setForm({ ...form, avatarUrl: e.target.value })}
                    />
                  </div>
                </Field>
              </div>
            </Section>
          </div>

          <div id="domains">
            <Section
              title="Where the widget can run"
              description="The widget refuses to load on origins not in this list. Subdomains are matched (example.com covers www.example.com)."
              actions={
                <button
                  type="button"
                  className="btn-primary"
                  onClick={saveDomains}
                  disabled={agentMut.isPending}
                >
                  {agentMut.isPending ? 'Saving…' : 'Save domains'}
                </button>
              }
            >
              <Field label="Allowed domains">
                <DomainsEditor
                  value={form.allowedDomains}
                  onChange={(d) => setForm({ ...form, allowedDomains: d })}
                />
              </Field>
              <Field
                label="Lead notification email"
                hint="Where new lead emails are sent. Leave blank to disable."
              >
                <input
                  type="email"
                  className="input"
                  value={form.notificationEmail}
                  onChange={(e) => setForm({ ...form, notificationEmail: e.target.value })}
                />
              </Field>
            </Section>
          </div>

          <div id="integrations">
            <Section
              title="Lead forwarding and measurement"
              description="Where a captured lead goes after the email, and what the page reports back to your ad platforms."
              actions={
                <button
                  type="button"
                  className="btn-primary"
                  onClick={saveIntegrations}
                  disabled={agentMut.isPending}
                >
                  {agentMut.isPending ? 'Saving…' : 'Save integrations'}
                </button>
              }
            >
              <Field
                label="Lead webhook URL"
                hint="Each captured lead is POSTed here as JSON. Leave blank to disable. Delivery status shows on the lead."
              >
                <input
                  type="url"
                  className="input"
                  placeholder="https://app.example.com/api/ingest/lead"
                  value={form.notificationWebhook}
                  onChange={(e) => setForm({ ...form, notificationWebhook: e.target.value })}
                />
              </Field>
              <Field
                label="Webhook API key"
                hint={
                  form.webhookSecret === SECRET_UNCHANGED
                    ? 'A key is saved. Replace it to update, or clear the field to remove it.'
                    : 'Sent as the x-api-key header. Stored encrypted and never shown again.'
                }
              >
                <input
                  type="password"
                  className="input"
                  autoComplete="off"
                  value={form.webhookSecret}
                  onChange={(e) => setForm({ ...form, webhookSecret: e.target.value })}
                />
              </Field>
              <Field
                label="Who sends the emails"
                hint="Some receivers email on their own behalf when a lead lands. Tick this and replai stays quiet, so a lead does not arrive twice."
              >
                <label className="flex items-start gap-2.5 text-[13px] text-ink">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={form.emailsHandledExternally}
                    onChange={(e) =>
                      setForm({ ...form, emailsHandledExternally: e.target.checked })
                    }
                  />
                  <span>
                    The webhook receiver sends the lead notification and the visitor
                    confirmation — replai should send neither.
                  </span>
                </label>
              </Field>
              <Field
                label="Form name"
                hint="Identifies chat leads in the receiving system. Defaults to the tenant and agent name."
              >
                <input
                  type="text"
                  className="input"
                  placeholder="Incline Homes — LP chat"
                  value={form.webhookFormName}
                  onChange={(e) => setForm({ ...form, webhookFormName: e.target.value })}
                />
              </Field>
              <Field
                label="Attribution cookies"
                hint="One per line. The widget reads these first-party cookies from the host page so ad clicks stay attributable. Use name:field to rename, e.g. ih_attr:attribution."
              >
                <textarea
                  className="input min-h-[84px] font-mono text-[12px]"
                  spellCheck={false}
                  placeholder={'ih_attr:attribution\nih_ab:experiment\nih_fb_eid:fb_event_id'}
                  value={form.attributionCookies}
                  onChange={(e) => setForm({ ...form, attributionCookies: e.target.value })}
                />
              </Field>
              <Field
                label="Conversion event"
                hint="Pushed to the page's dataLayer when a lead is captured, for Google Tag Manager to pick up. Leave blank to push nothing."
              >
                <input
                  type="text"
                  className="input"
                  placeholder="lead_chat"
                  value={form.conversionEventName}
                  onChange={(e) => setForm({ ...form, conversionEventName: e.target.value })}
                />
              </Field>
              <Field
                label="Redirect after capture"
                hint="Optional. Sends the visitor to a thank-you page a few seconds after the confirmation. Only needed if your conversion is anchored on a page view rather than an event."
              >
                <input
                  type="url"
                  className="input"
                  placeholder="https://example.com/thank-you/?src=chat"
                  value={form.conversionRedirectUrl}
                  onChange={(e) => setForm({ ...form, conversionRedirectUrl: e.target.value })}
                />
              </Field>
            </Section>
          </div>

          <div id="hours">
            <Section
              title="Team availability"
              description="Drives the message the bot uses after capturing a lead — both the timing it promises and the exact wording, if you customize it."
              actions={
                <button
                  type="button"
                  className="btn-primary"
                  onClick={saveAvailability}
                  disabled={tenantMut.isPending}
                >
                  {tenantMut.isPending ? 'Saving…' : 'Save availability'}
                </button>
              }
            >
              <Field label="Office hours">
                <WorkHoursEditor
                  value={form.workHours}
                  onChange={(wh) => setForm({ ...form, workHours: wh })}
                />
              </Field>

              <Field
                label="Promised reply time"
                hint={`Bot will say "within the next ${humanize(form.responseTimeMinutes)}" during office hours.`}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={form.responseTimeUnit === 'hr' ? 24 : 1440}
                    value={
                      form.responseTimeUnit === 'hr'
                        ? form.responseTimeMinutes / 60
                        : form.responseTimeMinutes
                    }
                    onChange={(e) => {
                      const n = parseInt(e.target.value || '0', 10);
                      const minutes =
                        form.responseTimeUnit === 'hr'
                          ? Math.max(1, n) * 60
                          : Math.max(1, n);
                      setForm({ ...form, responseTimeMinutes: minutes });
                    }}
                    className="input w-[88px] text-center"
                  />
                  <div className="inline-flex p-0.5 bg-[#F2F2F5] rounded-md">
                    {(['min', 'hr'] as const).map((u) => {
                      const on = form.responseTimeUnit === u;
                      return (
                        <button
                          key={u}
                          type="button"
                          onClick={() => {
                            if (on) return;
                            // Convert the displayed value to keep it sensible across the unit flip.
                            // Switching min → hr: snap up to the next whole hour if minutes aren't a multiple.
                            const nextMinutes =
                              u === 'hr'
                                ? Math.max(60, Math.round(form.responseTimeMinutes / 60) * 60)
                                : form.responseTimeMinutes;
                            setForm({
                              ...form,
                              responseTimeUnit: u,
                              responseTimeMinutes: nextMinutes,
                            });
                          }}
                          className={`px-2.5 py-1 text-[11.5px] font-medium rounded ${
                            on ? 'bg-white text-ink shadow-soft' : 'text-ink-muted hover:text-ink'
                          }`}
                        >
                          {u === 'min' ? 'min' : 'hr'}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </Field>

              <Field
                label="Closing message — inside office hours"
                hint="Optional. Leave blank to use the default. Supports {timing}, which becomes e.g. '20 minutes' or '2 hours'."
              >
                <textarea
                  className="input min-h-[64px]"
                  maxLength={500}
                  placeholder={`Default: "the team will reach out within the next ${humanize(form.responseTimeMinutes)}"`}
                  value={form.inHoursReplyMessage}
                  onChange={(e) => setForm({ ...form, inHoursReplyMessage: e.target.value })}
                />
              </Field>

              <Field
                label="Closing message — outside office hours"
                hint="Optional. Leave blank to use the default. No timing here — out-of-hours we don't promise a window."
              >
                <textarea
                  className="input min-h-[64px]"
                  maxLength={500}
                  placeholder={'Default: "the team will reach out when office hours resume"'}
                  value={form.outOfHoursReplyMessage}
                  onChange={(e) => setForm({ ...form, outOfHoursReplyMessage: e.target.value })}
                />
              </Field>
            </Section>
          </div>

          <div id="schedule">
            <Section
              title="Widget schedule"
              description="Optional: restrict when the chat is reachable. Outside these hours the widget shows your offline message."
              actions={
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-[12.5px] text-ink-soft">
                    <input
                      type="checkbox"
                      checked={!!form.widgetSchedule}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          widgetSchedule: e.target.checked ? blankSchedule() : null,
                        })
                      }
                    />
                    Enable schedule
                  </label>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={saveSchedule}
                    disabled={agentMut.isPending}
                  >
                    {agentMut.isPending ? 'Saving…' : 'Save'}
                  </button>
                </div>
              }
            >
              {form.widgetSchedule ? (
                <>
                  <Field label="When the widget is reachable">
                    <WidgetScheduleEditor
                      value={form.widgetSchedule}
                      onChange={(ws) => setForm({ ...form, widgetSchedule: ws })}
                    />
                  </Field>
                  <Field
                    label="Offline message"
                    hint="Shown to visitors who arrive outside schedule."
                  >
                    <textarea
                      className="input min-h-[64px]"
                      value={form.offlineMessage}
                      onChange={(e) => setForm({ ...form, offlineMessage: e.target.value })}
                    />
                  </Field>
                </>
              ) : (
                <p className="text-[12.5px] text-ink-subtle italic">
                  Widget is reachable 24/7 — enable a schedule to limit hours.
                </p>
              )}
            </Section>
          </div>

          <div id="sender">
            <Section
              title="Lead emails — sender"
              description="By default lead notifications come from the replai platform. Use your own Resend account if you want them sent from your domain."
              actions={
                <button
                  type="button"
                  className="btn-primary"
                  onClick={saveEmailSender}
                  disabled={tenantMut.isPending}
                >
                  {tenantMut.isPending ? 'Saving…' : 'Save sender'}
                </button>
              }
            >
              <Field
                label="Resend API key"
                hint={
                  form.resendApiKey === RESEND_UNCHANGED && tenant.resendApiKeySet
                    ? `Saved (preview: ${tenant.resendApiKeyMasked}). Replace to update, or clear to revert to platform default.`
                    : 'Optional. Leave blank to use the replai platform key.'
                }
              >
                <div className="flex items-center gap-2">
                  <input
                    className="input"
                    placeholder={tenant.resendApiKeySet ? '(unchanged)' : 're_…'}
                    value={form.resendApiKey === RESEND_UNCHANGED ? '' : form.resendApiKey}
                    onChange={(e) => setForm({ ...form, resendApiKey: e.target.value })}
                  />
                  {tenant.resendApiKeySet && (
                    <button
                      type="button"
                      className="btn-danger"
                      onClick={() => setForm({ ...form, resendApiKey: '' })}
                      title="Clear (revert to platform default)"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </Field>
              <Field label="From address" hint="Must be verified in your Resend account.">
                <input
                  type="email"
                  className="input"
                  placeholder="hello@yourdomain.com"
                  value={form.resendFrom}
                  onChange={(e) => setForm({ ...form, resendFrom: e.target.value })}
                />
              </Field>
            </Section>
          </div>

          <div id="embed">
            <Section
              title="Embed snippet"
              description="Drop this on your site to enable the chat."
              actions={
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => {
                    navigator.clipboard.writeText(embedSnippet(agent));
                    toast.success('Embed snippet copied');
                  }}
                >
                  <Icon.Copy size={13} />
                  Copy
                </button>
              }
            >
              <pre className="m-0 p-3.5 rounded-[10px] bg-ink text-[#C7CADB] font-mono text-[11.5px] leading-relaxed overflow-x-auto whitespace-pre">
                {embedSnippet(agent)}
              </pre>
            </Section>
          </div>
        </div>

        {/* Right: sticky preview + diagnostics */}
        <aside className="xl:sticky xl:top-4 flex flex-col gap-3.5">
          <section className="card overflow-hidden">
            <div className="px-4 py-3.5 border-b border-line flex items-center justify-between">
              <div className="text-[12.5px] font-semibold text-ink">Live preview</div>
              <Pill tone="accent">syncs as you type</Pill>
            </div>
            <WidgetPreview form={form} />
          </section>

          <Diagnostics
            agent={agent}
            tenant={tenant}
            workHoursSet={hasWorkHours(form.workHours)}
            kbConfigured={null}
          />
        </aside>
      </div>
    </div>
  );
};

// ---- Section nav (scroll-anchored, no save semantics change) ----

const SECTIONS = [
  { id: 'basics', label: 'Basics' },
  { id: 'domains', label: 'Domains' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'hours', label: 'Hours' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'sender', label: 'Sender' },
  { id: 'embed', label: 'Embed' },
];

const SectionTabs = ({ sections }: { sections: { id: string; label: string }[] }) => (
  <div className="flex gap-1 p-1 bg-[#F2F2F5] rounded-[10px] w-fit">
    {sections.map((s) => (
      <a
        key={s.id}
        href={`#${s.id}`}
        className="px-3.5 py-1.5 rounded-md text-xs font-medium text-ink-muted hover:bg-white hover:text-ink transition-colors"
      >
        {s.label}
      </a>
    ))}
  </div>
);

// ---- Live widget preview ----

const WidgetPreview = ({ form }: { form: FormState }) => {
  const initial = form.name.trim().charAt(0).toUpperCase() || 'A';
  return (
    <div className="p-4 bg-[#FAFAFB]">
      <div
        className="bg-white rounded-[14px] overflow-hidden border border-line"
        style={{ boxShadow: '0 16px 40px -16px rgba(15,15,40,.25)' }}
      >
        {/* header */}
        <div
          className="px-3.5 py-3 text-white flex items-center gap-2.5"
          style={{ background: form.primary }}
        >
          <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center font-serif text-sm">
            {form.avatarUrl ? (
              <img src={form.avatarUrl} alt="" className="w-full h-full rounded-full object-cover" />
            ) : (
              initial
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-semibold leading-tight truncate">{form.name}</div>
            <div className="text-[10.5px] opacity-90 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-300" />
              Online · replies in ~{form.responseTimeMinutes}m
            </div>
          </div>
          <Icon.X size={14} />
        </div>

        {/* messages */}
        <div className="p-3.5 flex flex-col gap-2 min-h-[200px] bg-[#FFFDFD]">
          <Bubble bot avatar={initial} accent={form.primary}>
            {form.welcome || 'Hi! How can I help today?'}
          </Bubble>
          <Bubble>Hi, do you take walk-ins this evening?</Bubble>
          <Bubble bot avatar={initial} accent={form.primary} typing />
        </div>

        {/* footer */}
        <div className="border-t border-line p-2 flex gap-2 items-center">
          <input
            placeholder="Type a message…"
            disabled
            className="flex-1 border-0 outline-none text-xs px-1 py-1.5 bg-transparent text-ink-subtle"
          />
          <button
            type="button"
            disabled
            className="w-7 h-7 rounded-full text-white flex items-center justify-center opacity-90 cursor-default"
            style={{ background: form.primary }}
          >
            <Icon.ArrowUpRight size={12} />
          </button>
        </div>
      </div>
      <div className="text-[10.5px] text-ink-subtle text-center mt-2">Powered by replai</div>
    </div>
  );
};

const Bubble = ({
  bot,
  children,
  avatar,
  accent,
  typing,
}: {
  bot?: boolean;
  children?: React.ReactNode;
  avatar?: string;
  accent?: string;
  typing?: boolean;
}) => {
  if (bot) {
    return (
      <div className="flex gap-1.5 items-start">
        <div
          className="w-[22px] h-[22px] rounded-full text-white flex items-center justify-center text-[10.5px] font-semibold font-serif shrink-0"
          style={{ background: accent }}
        >
          {avatar}
        </div>
        <div className="bg-[#F4F4F7] text-ink py-2 px-2.5 rounded-[12px_12px_12px_4px] text-xs leading-relaxed max-w-[85%]">
          {typing ? <TypingDots /> : children}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-end">
      <div
        className="text-white py-2 px-2.5 rounded-[12px_12px_4px_12px] text-xs leading-relaxed max-w-[85%]"
        style={{ background: '#1F2024' }}
      >
        {children}
      </div>
    </div>
  );
};

const TypingDots = () => (
  <span className="inline-flex gap-1 py-1 px-0.5">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="w-[5px] h-[5px] rounded-full bg-ink-subtle animate-typing-bounce"
        style={{ animationDelay: `${i * 0.15}s` }}
      />
    ))}
  </span>
);

// ---- Diagnostics ----

const Diagnostics = ({
  agent,
  tenant,
  workHoursSet,
  kbConfigured,
}: {
  agent: Agent;
  tenant: Tenant;
  workHoursSet: boolean;
  kbConfigured: boolean | null;
}) => {
  const rows: { key: string; ok: boolean; pendingLabel?: string }[] = [
    { key: 'Widget active', ok: agent.status === 'ACTIVE' },
    { key: 'Allowed domains set', ok: (agent.allowedDomains?.length ?? 0) > 0 },
    { key: 'Office hours set', ok: workHoursSet },
    { key: 'Notification email', ok: !!agent.notificationEmail },
    { key: 'Custom email sender', ok: tenant.resendApiKeySet && !!tenant.resendFrom, pendingLabel: 'Optional' },
  ];
  if (kbConfigured !== null) {
    rows.push({ key: 'Knowledge base ready', ok: kbConfigured });
  }

  return (
    <section className="card p-[18px]">
      <div className="text-[12.5px] font-semibold text-ink mb-2.5">Diagnostics</div>
      <div className="flex flex-col gap-2">
        {rows.map((r) => (
          <div key={r.key} className="flex justify-between text-xs">
            <span className="text-ink-muted">{r.key}</span>
            <span
              className={`inline-flex items-center gap-1.5 font-medium ${
                r.ok ? 'text-emerald-700' : 'text-amber-700'
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  r.ok ? 'bg-emerald-500' : 'bg-amber-500'
                }`}
              />
              {r.ok ? 'Healthy' : r.pendingLabel ?? 'Review'}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

// ---- form helpers (unchanged from previous version) ----

const RESEND_UNCHANGED = '__UNCHANGED__';

// Sentinel for a stored write-only secret: shown as-is, and only sent to the API when
// the operator actually replaces it.
const SECRET_UNCHANGED = '••••••••••••';

// "name" or "name:field" per line -> [{ name, as }]. Blank lines ignored.
const parseCookieLines = (raw: string): { name: string; as?: string }[] =>
  raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [name, as] = line.split(':').map((p) => p.trim());
      return as ? { name, as } : { name };
    })
    .filter((c) => c.name.length > 0);

// Mirrors the server's humanizeMinutes so the UI preview matches what the bot will say.
const humanize = (minutes: number): string => {
  if (!Number.isFinite(minutes) || minutes <= 0) return '20 minutes';
  if (minutes < 60 || minutes % 60 !== 0) {
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }
  const hours = minutes / 60;
  return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
};

const PERSONA_SUGGESTIONS = [
  { text: '+ Be more formal', append: 'Be more formal with returning customers.' },
  { text: '+ Keep replies short', append: 'Keep replies to two sentences max unless asked for detail.' },
  { text: '+ Add booking nudges', append: 'Gently nudge toward booking when intent is clear.' },
];

interface FormState {
  name: string;
  welcome: string;
  persona: string;
  primary: string;
  avatarUrl: string;
  launcherStyle: 'bubble' | 'label';
  launcherLabel: string;
  offsetBottom: number;
  offsetBottomBelow: string;
  allowedDomains: string[];
  notificationEmail: string;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED';
  offlineMessage: string;
  widgetSchedule: WidgetSchedule | null;
  workHours: WorkHours | null;
  responseTimeMinutes: number;
  resendApiKey: string;
  resendFrom: string;
  notificationWebhook: string;
  webhookSecret: string;
  webhookFormName: string;
  attributionCookies: string;
  conversionEventName: string;
  conversionRedirectUrl: string;
  emailsHandledExternally: boolean;
  inHoursReplyMessage: string;
  outOfHoursReplyMessage: string;
  responseTimeUnit: 'min' | 'hr';
}

const toFormState = (a: Agent, t: Tenant): FormState => ({
  name: a.name,
  welcome: a.widgetWelcomeMsg ?? '',
  persona: a.persona ?? '',
  primary: a.widgetPrimaryColor ?? '#1F2329',
  avatarUrl: a.widgetAvatarUrl ?? '',
  launcherStyle: a.widgetLauncherStyle === 'label' ? 'label' : 'bubble',
  launcherLabel: a.widgetLauncherLabel ?? '',
  offsetBottom: a.widgetOffsetBottom ?? 0,
  offsetBottomBelow: a.widgetOffsetBottomBelow ? String(a.widgetOffsetBottomBelow) : '',
  allowedDomains: a.allowedDomains ?? [],
  notificationEmail: a.notificationEmail ?? '',
  status: a.status,
  offlineMessage: a.widgetOfflineMessage ?? '',
  widgetSchedule: a.widgetSchedule,
  workHours: t.workHours,
  responseTimeMinutes: t.responseTimeMinutes,
  resendApiKey: t.resendApiKeySet ? RESEND_UNCHANGED : '',
  resendFrom: t.resendFrom ?? '',
  notificationWebhook: a.notificationWebhook ?? '',
  webhookSecret: a.notificationWebhookSecretSet ? SECRET_UNCHANGED : '',
  webhookFormName: a.webhookFormName ?? '',
  attributionCookies: (a.attributionCookies ?? [])
    .map((c) => (c.as && c.as !== c.name ? `${c.name}:${c.as}` : c.name))
    .join('\n'),
  conversionEventName: a.conversionEventName ?? '',
  conversionRedirectUrl: a.conversionRedirectUrl ?? '',
  emailsHandledExternally: a.emailsHandledExternally ?? false,
  inHoursReplyMessage: t.inHoursReplyMessage ?? '',
  outOfHoursReplyMessage: t.outOfHoursReplyMessage ?? '',
  // Prefer hours when the stored value divides cleanly — operator probably set it that way.
  responseTimeUnit:
    t.responseTimeMinutes >= 60 && t.responseTimeMinutes % 60 === 0 ? 'hr' : 'min',
});

const blankSchedule = (): WidgetSchedule => ({
  tz: 'Asia/Singapore',
  days: {
    mon: [[9, 18]],
    tue: [[9, 18]],
    wed: [[9, 18]],
    thu: [[9, 18]],
    fri: [[9, 18]],
    sat: null,
    sun: null,
  },
});

const hasWorkHours = (wh: WorkHours | null): boolean => {
  if (!wh || !wh.weekdays) return false;
  return Object.values(wh.weekdays).some((interval) => interval !== null && interval !== undefined);
};

const embedSnippet = (a: Agent) => `<script
  src="https://widget.replai.axccelerate.com/widget.js"
  data-agent-key="${a.apiKey}"
  async
></script>`;

const DAYS: { key: DayKey; label: string }[] = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

const WidgetScheduleEditor = ({
  value,
  onChange,
}: {
  value: WidgetSchedule;
  onChange: (next: WidgetSchedule) => void;
}) => {
  const days = value.days ?? {};
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {DAYS.map(({ key, label }) => {
        const intervals = days[key];
        const closed = intervals == null || intervals.length === 0;
        const open = intervals?.[0]?.[0] ?? 9;
        const close = intervals?.[0]?.[1] ?? 18;
        return (
          <div
            key={key}
            className="flex items-center gap-2 border border-line rounded-md px-3 py-2 bg-white"
          >
            <div className="w-10 text-[11.5px] text-ink-soft font-medium">{label}</div>
            <label className="flex items-center gap-1.5 text-[11.5px] text-ink-muted">
              <input
                type="checkbox"
                checked={!closed}
                onChange={(e) => {
                  const next = {
                    ...days,
                    [key]: e.target.checked
                      ? ([[open, close]] as Array<[number, number]>)
                      : null,
                  };
                  onChange({ ...value, days: next });
                }}
              />
              Open
            </label>
            {!closed && intervals && (
              <>
                <HourBox
                  v={intervals[0][0]}
                  onV={(h) =>
                    onChange({
                      ...value,
                      days: {
                        ...days,
                        [key]: [[Math.min(h, intervals[0][1] - 1), intervals[0][1]]],
                      },
                    })
                  }
                />
                <span className="text-ink-subtle text-[11.5px]">–</span>
                <HourBox
                  v={intervals[0][1]}
                  onV={(h) =>
                    onChange({
                      ...value,
                      days: {
                        ...days,
                        [key]: [[intervals[0][0], Math.max(h, intervals[0][0] + 1)]],
                      },
                    })
                  }
                />
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

const HourBox = ({ v, onV }: { v: number; onV: (h: number) => void }) => (
  <input
    type="number"
    min={0}
    max={24}
    value={v}
    onChange={(e) => onV(Math.max(0, Math.min(24, parseInt(e.target.value || '0', 10))))}
    className="input w-14 py-1 text-sm text-center"
  />
);
